import { createFileRoute, Outlet } from '@tanstack/react-router'
import { lazy, Suspense, useLayoutEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { StatusBar } from '@capacitor/status-bar'
import { env } from '@/config/env'
import { assetPath } from '@/utils/assetPath'
import { useSafeAreaStore } from '@/stores/safeArea'

const AdaptiveVideo = lazy(() => import('@/components/shared/AdaptiveVideo'))

export const Route = createFileRoute('/(onboarding)')({
    component: RouteComponent,
})

function RouteComponent() {
    return <OnboardingLayout />
}

function OnboardingLayout() {
    const setShowStatusBarCover = useSafeAreaStore((s) => s.setShowStatusBarCover)

    // Opt out of the root layout's status bar cover for edge-to-edge video
    // Using useLayoutEffect to set state before browser paint, avoiding double-padding flicker
    useLayoutEffect(() => {
        setShowStatusBarCover(false)

        // On Android, make status bar transparent for edge-to-edge video
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
            StatusBar.setBackgroundColor({ color: '#00000000' }) // Transparent
        }

        return () => {
            setShowStatusBarCover(true)

            // Reset Android status bar to white when leaving onboarding
            if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
                StatusBar.setBackgroundColor({ color: '#FFFFFF' }) // White
            }
        }
    }, [setShowStatusBarCover])

    return (
        <div className="w-full">
            {/* Video background - goes edge-to-edge behind status bar */}
            <div className="pointer-events-none fixed inset-0 h-full w-full">
                <Suspense
                    fallback={
                        <div className="h-full w-full bg-gradient-to-b from-purple-900 to-black" />
                    }
                >
                    <AdaptiveVideo
                        basePath={env.R2_ASSETS_URL}
                        videoName="assets/videos/onboarding-background"
                        poster={assetPath("images/onboarding-poster.webp")}
                        className="h-full w-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                </Suspense>
            </div>

            {/* Content wrapper - each page handles its own pt-safe */}
            <div className="relative min-h-dvh">
                <Outlet />
            </div>
        </div>
    )
}
